import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import WhatsAppWidget from '../components/WhatsAppWidget';
import PhoneInput from '../components/PhoneInput';
import CountrySelect from '../components/CountrySelect';

const API_BASE = process.env.REACT_APP_API_BASE_URL || `${process.env.FRONTEND_URL}/api`;

const JOB_DOCS = [
  { key: 'passport', label: 'Passport Copy', required: true },
  { key: 'cv', label: 'CV/Resume', required: true },
  { key: 'tradeCertifications', label: 'Trade Certifications', required: false },
  { key: 'certificate', label: 'Academic/High School Certificate', required: true },
  { key: 'photo', label: 'Passport-size Photo', required: true },
];

export default function AssessmentPortalJob() {
  const [searchParams] = useSearchParams();
  const opportunityId = searchParams.get('opportunityId') || null;
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [personal, setPersonal] = useState({
    fullName: '', email: '', phone: '', whatsapp: '',
    nationality: '', dateOfBirth: '', gender: '', address: '',
    passportNumber: '', passportExpiry: '',
    emergencyContactName: '', emergencyContactPhone: '',
  });

  const [jobFields, setJobFields] = useState({
    targetCountry: '', sector: '', roleAppliedFor: '', workExperienceYears: '',
    tradeCertifications: '', languageAbility: '', priorTravelHistory: '',
    visaRefusalHistory: '', medicalExamConsent: false, availabilityDate: '',
  });

  const [files, setFiles] = useState({});

  const handleFileChange = (key, fileObj) => setFiles((prev) => ({ ...prev, [key]: fileObj }));

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const formResponses = {
        ...jobFields,
        tradeCertifications: jobFields.tradeCertifications ? [jobFields.tradeCertifications] : [],
        languageAbility: jobFields.languageAbility ? [jobFields.languageAbility] : [],
        passportNumber: personal.passportNumber,
        passportExpiry: personal.passportExpiry,
        emergencyContactName: personal.emergencyContactName,
        emergencyContactPhone: personal.emergencyContactPhone,
      };

      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/applications`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ type: 'JOB', opportunityId, personal, formResponses }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error?.message || 'Submission failed');

      const applicationId = json.data.id;

      for (const doc of JOB_DOCS) {
        const file = files[doc.key];
        if (!file) continue;
        const fd = new FormData();
        fd.append('documentType', doc.label);
        fd.append('file', file);
        await fetch(`${API_BASE}/applications/${applicationId}/documents`, {
          method: 'POST',
          headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: fd,
        });
      }

      setSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-[#f7f9fb] font-['Plus_Jakarta_Sans'] min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 flex items-center justify-center pt-32 pb-20 px-4">
          <div className="text-center max-w-md">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <span className="material-symbols-outlined text-5xl text-green-600">check_circle</span>
            </div>
            <h2 className="text-3xl font-bold text-[#00255a] mb-4">Application Received!</h2>
            <p className="text-[#64748b] mb-8">
              An agent will contact you shortly to guide you through the next steps of your work placement application.
            </p>
            <button
              onClick={() => navigate('/')}
              className="text-white px-8 py-4 rounded-xl font-semibold"
              style={{ background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 60%, #ff7b7b 100%)' }}
            >
              Return to Homepage
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-[#f7f9fb] font-['Plus_Jakarta_Sans'] min-h-screen">
      <Navbar />
      <main className="pt-32 pb-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#ffdad8] text-[#862026] text-sm font-semibold mb-2">
              Work Placement Application
            </span>
          </div>

          <div className="mb-12">
            <div className="flex justify-between items-center relative">
              <div className="absolute top-1/2 left-0 w-full h-0.5 bg-[#c4c6d1] -translate-y-1/2 z-0" />
              <div
                className="absolute top-1/2 left-0 h-0.5 -translate-y-1/2 z-0 transition-all duration-500"
                style={{ width: `${((step - 1) / 2) * 100}%`, background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 60%, #ff7b7b 100%)' }}
              />
              {['Personal Info', 'Job Preferences', 'Documents'].map((label, i) => {
                const n = i + 1;
                const active = n <= step;
                return (
                  <div key={label} className="relative z-10 flex flex-col items-center">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all ${
                        active ? 'text-white shadow-md' : 'bg-[#e0e3e5] text-[#44474f] border-2 border-[#c4c6d1]'
                      }`}
                      style={active ? { background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 60%, #ff7b7b 100%)' } : {}}
                    >
                      {n < step ? <span className="material-symbols-outlined text-sm">check</span> : n}
                    </div>
                    <span className={`mt-2 text-sm ${active ? 'text-[#00255a] font-bold' : 'text-[#64748b]'}`}>{label}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-[#e2e8f0] p-8 md:p-12 shadow-[0px_10px_30px_rgba(30,60,114,0.05)] min-h-[500px] flex flex-col">
            {error && <div className="mb-6 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">{error}</div>}

            {step === 1 && (
              <div>
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-[#0f172a] mb-2">Personal Information</h2>
                  <p className="text-[#64748b]">Please provide your official contact details.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field label="Full Name" value={personal.fullName} onChange={(v) => setPersonal({ ...personal, fullName: v })} />
                  <Field label="Email Address" type="email" value={personal.email} onChange={(v) => setPersonal({ ...personal, email: v })} />
                  <PhoneInput label="Phone Number" value={personal.phone} onChange={(v) => setPersonal({ ...personal, phone: v })} />
                  <PhoneInput label="WhatsApp Number" value={personal.whatsapp} onChange={(v) => setPersonal({ ...personal, whatsapp: v })} />
                  <Field label="Nationality" value={personal.nationality} onChange={(v) => setPersonal({ ...personal, nationality: v })} />
                  <Field label="Date of Birth" type="date" value={personal.dateOfBirth} onChange={(v) => setPersonal({ ...personal, dateOfBirth: v })} />
                  <SelectField label="Gender" value={personal.gender} onChange={(v) => setPersonal({ ...personal, gender: v })} options={['Male', 'Female', 'Prefer not to say']} />
                  <Field label="Passport Number" value={personal.passportNumber} onChange={(v) => setPersonal({ ...personal, passportNumber: v })} />
                  <Field label="Passport Expiry" type="date" value={personal.passportExpiry} onChange={(v) => setPersonal({ ...personal, passportExpiry: v })} />
                  <Field label="Emergency Contact Name" value={personal.emergencyContactName} onChange={(v) => setPersonal({ ...personal, emergencyContactName: v })} />
                  <PhoneInput label="Emergency Contact Phone" value={personal.emergencyContactPhone} onChange={(v) => setPersonal({ ...personal, emergencyContactPhone: v })} />
                  <div className="md:col-span-2">
                    <Field label="Home Address" value={personal.address} onChange={(v) => setPersonal({ ...personal, address: v })} />
                  </div>
                </div>
              </div>
            )}

            {step === 2 && (
              <div>
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-[#0f172a] mb-2">Job Preferences</h2>
                  <p className="text-[#64748b]">Tell us about the role you're targeting.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex flex-col gap-2"><label className="text-sm font-semibold text-[#44474f]">Target Country</label><CountrySelect required value={jobFields.targetCountry} onChange={(v) => setJobFields({ ...jobFields, targetCountry: v })} className="w-full px-4 py-3 rounded-xl border border-[#c4c6d1] bg-[#f7f9fb] focus:border-[#00255a] focus:ring-2 focus:ring-[#00255a]/10 outline-none transition-all" /></div>
                  <SelectField label="Sector" value={jobFields.sector} onChange={(v) => setJobFields({ ...jobFields, sector: v })} options={['Skilled Trades', 'Hospitality', 'Care Work', 'Manufacturing', 'Education', 'Other']} />
                  <Field label="Role Applied For" value={jobFields.roleAppliedFor} onChange={(v) => setJobFields({ ...jobFields, roleAppliedFor: v })} />
                  <Field label="Years of Work Experience" type="number" value={jobFields.workExperienceYears} onChange={(v) => setJobFields({ ...jobFields, workExperienceYears: v })} />
                  <Field label="Trade Certifications" value={jobFields.tradeCertifications} onChange={(v) => setJobFields({ ...jobFields, tradeCertifications: v })} placeholder="Optional" />
                  <Field label="Language Ability" value={jobFields.languageAbility} onChange={(v) => setJobFields({ ...jobFields, languageAbility: v })} placeholder="e.g. English, French" />
                  <Field label="Prior Travel History" value={jobFields.priorTravelHistory} onChange={(v) => setJobFields({ ...jobFields, priorTravelHistory: v })} placeholder="Optional" />
                  <Field label="Visa Refusal History" value={jobFields.visaRefusalHistory} onChange={(v) => setJobFields({ ...jobFields, visaRefusalHistory: v })} placeholder="Optional" />
                  <Field label="Availability Date" type="date" value={jobFields.availabilityDate} onChange={(v) => setJobFields({ ...jobFields, availabilityDate: v })} />
                  <label className="flex items-center gap-2 mt-2">
                    <input type="checkbox" checked={jobFields.medicalExamConsent} onChange={(e) => setJobFields({ ...jobFields, medicalExamConsent: e.target.checked })} className="w-4 h-4 rounded border-[#c4c6d1]" />
                    <span className="text-sm text-[#44474f]">Willing to undergo a medical exam</span>
                  </label>
                </div>
              </div>
            )}

            {step === 3 && (
              <div>
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-[#0f172a] mb-2">Upload Documents</h2>
                  <p className="text-[#64748b]">Max 10MB per file. Accepted formats: PDF, JPG, JPEG, PNG, WEBP.</p>
                </div>
                <div className="space-y-4">
                  {JOB_DOCS.map((doc) => (
                    <div key={doc.key} className="flex items-center justify-between p-4 rounded-xl border border-[#e2e8f0] bg-[#f7f9fb]">
                      <div>
                        <p className="font-semibold text-[#0f172a]">{doc.label} {doc.required && <span className="text-red-500">*</span>}</p>
                        {files[doc.key] && <p className="text-xs text-[#64748b] mt-1">{files[doc.key].name}</p>}
                      </div>
                      <label className="cursor-pointer px-4 py-2 bg-white border border-[#e2e8f0] rounded-lg text-sm font-semibold text-[#00255a] hover:bg-[#f2f4f6]">
                        {files[doc.key] ? 'Replace' : 'Upload'}
                        <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={(e) => handleFileChange(doc.key, e.target.files[0])} />
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-12 flex justify-between items-center border-t border-[#e2e8f0] pt-8">
              {step > 1 ? (
                <button onClick={() => setStep(step - 1)} className="px-8 py-3 rounded-full border-2 border-[#c4c6d1] text-[#44474f] font-semibold hover:border-[#00255a] hover:text-[#00255a] transition-all flex items-center gap-2">
                  <span className="material-symbols-outlined text-sm">arrow_back</span> Back
                </button>
              ) : <div />}
              <button
                onClick={() => (step < 3 ? setStep(step + 1) : handleSubmit())}
                disabled={submitting}
                className="px-10 py-3 rounded-full text-white font-semibold shadow-lg hover:scale-105 transition-all flex items-center gap-2 disabled:opacity-60"
                style={{ background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 60%, #ff7b7b 100%)' }}
              >
                {step < 3 ? (<>Continue <span className="material-symbols-outlined text-sm">arrow_forward</span></>) : submitting ? 'Submitting...' : 'Submit Application'}
              </button>
            </div>
          </div>
        </div>
      </main>
      <Footer />
      <WhatsAppWidget />
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', placeholder }) {
  return (
    <div className="flex flex-col gap-2">
      <label className="text-sm font-semibold text-[#44474f]">{label}</label>
      <input type={type} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className="w-full px-4 py-3 rounded-xl border border-[#c4c6d1] bg-[#f7f9fb] focus:border-[#00255a] focus:ring-2 focus:ring-[#00255a]/10 outline-none transition-all" />
    </div>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <div className="flex flex-col gap-2">
      <label className="text-sm font-semibold text-[#44474f]">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full px-4 py-3 rounded-xl border border-[#c4c6d1] bg-[#f7f9fb] focus:border-[#00255a] focus:ring-2 focus:ring-[#00255a]/10 outline-none transition-all">
        <option value="">Select...</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
